import { formatDurationCompact } from '@aegislab/shared';
import type { WeeklyCompactDay, LeaveType, WeeklySummary } from '../../../types';
import { WEEKDAYS, getLocalWeekStart, getLocalDateString } from '../../../lib';
import * as s from './WeeklyAttendanceList.css';

const DEFAULT_DAILY_TARGET_MS = 8 * 3600_000;

/** Pad days array to always contain 7 days (Mon-Sun) */
function padToFullWeek(days: WeeklyCompactDay[]): WeeklyCompactDay[] {
  const refDate = days.length > 0 ? new Date((days[0] as WeeklyCompactDay).date) : new Date();
  const weekStart = getLocalWeekStart(refDate);
  const dayMap = new Map(days.map((d) => [d.date, d]));

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    const dateStr = getLocalDateString(date);
    return (
      dayMap.get(dateStr) ?? {
        date: dateStr,
        clockInAt: null,
        clockOutAt: null,
        totalWorkDurationMs: 0,
        isToday: false,
        isWorking: false,
      }
    );
  });
}

export interface WeeklyAttendanceListProps {
  days: WeeklyCompactDay[];
  dailyTargetMs?: number;
  summary?: WeeklySummary;
}

const LEAVE_SHORT: Record<LeaveType, string> = {
  annual: '연',
  sick: '병',
  special: '특',
};

function getWeekdayLabel(date: string): string {
  const parts = date.split('-');
  const dow = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getDay();
  return WEEKDAYS[dow] ?? '';
}

function pct(workMs: number, targetMs: number): number {
  if (targetMs <= 0) return 0;
  return Math.round((workMs / targetMs) * 100);
}

function barColorClass(percent: number, isWorking: boolean): string {
  if (isWorking) return s.barFillActive;
  if (percent >= 100) return s.barFillFull;
  if (percent >= 50) return s.barFillHalf;
  return s.barFillLow;
}

function buildAriaLabel(day: WeeklyCompactDay, targetMs: number): string {
  const weekday = getWeekdayLabel(day.date);
  if (day.leaveType) return `${weekday}: ${LEAVE_SHORT[day.leaveType]}`;
  if (!day.clockInAt) return `${weekday}: 미출근`;
  const percent = pct(day.totalWorkDurationMs, targetMs);
  const duration = formatDurationCompact(day.totalWorkDurationMs);
  return `${weekday}: ${duration} (${percent}%)${day.isWorking ? ' 근무 중' : ''}`;
}

export function WeeklyAttendanceList({
  days,
  dailyTargetMs = DEFAULT_DAILY_TARGET_MS,
  summary,
}: WeeklyAttendanceListProps) {
  const fullWeek = padToFullWeek(days);

  // Schedule-aware calculation when summary is provided, fallback to legacy
  const workDays = fullWeek.filter((d) => d.clockInAt && !d.leaveType);
  const totalWorkMs =
    summary?.totalWorkMs ?? workDays.reduce((sum, d) => sum + d.totalWorkDurationMs, 0);
  const weeklyTargetMs = summary?.weeklyTargetMs ?? workDays.length * dailyTargetMs;
  const weeklyPct = summary ? pct(totalWorkMs, weeklyTargetMs) : pct(totalWorkMs, weeklyTargetMs);

  return (
    <div className={s.container}>
      <div className={s.barChart} role="group" aria-label="주간 출근 현황">
        {fullWeek.map((day) => {
          const weekday = getWeekdayLabel(day.date);
          const hasLeave = !!day.leaveType;
          const notClockedIn = !day.clockInAt && !hasLeave;
          const percent = pct(day.totalWorkDurationMs, dailyTargetMs);
          const barHeight = Math.min(percent, 100);
          const isToday = !!day.isToday;
          const isWorking = !!day.isWorking;

          const durationLabel =
            !hasLeave && !notClockedIn && day.totalWorkDurationMs > 0
              ? formatDurationCompact(day.totalWorkDurationMs)
              : null;

          return (
            <div
              key={day.date}
              className={`${s.barColumn} ${isToday ? s.barColumnToday : ''}`}
              aria-label={buildAriaLabel(day, dailyTargetMs)}
            >
              {durationLabel && <span className={s.barDuration}>{durationLabel}</span>}
              <div className={s.barTrack}>
                {hasLeave ? (
                  <div className={s.barLeave}>
                    <span className={s.barLeaveLabel}>
                      {day.leaveType ? LEAVE_SHORT[day.leaveType] : ''}
                    </span>
                  </div>
                ) : notClockedIn ? (
                  <div className={s.barEmpty} />
                ) : (
                  <>
                    <div
                      className={`${s.barFill} ${barColorClass(percent, isWorking)}`}
                      style={{ height: `${barHeight}%` }}
                      role="meter"
                      aria-valuenow={percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${percent}%`}
                    />
                    {percent > 100 && (
                      <div
                        className={s.barFillOvertime}
                        style={{ height: `${Math.min(percent - 100, 30)}%` }}
                        aria-hidden="true"
                      />
                    )}
                  </>
                )}
                <div className={s.barTargetLine} />
              </div>

              <span className={`${s.barLabel} ${isToday ? s.barLabelToday : ''}`}>{weekday}</span>
              {day.correctionStatus === 'PENDING' && (
                // 의미를 담은 마커라 이름이 필요하다. 역할 없는 span 에는 aria-label 이
                // 금지(aria-prohibited-attr)라 role="img" 로 이름을 받을 수 있게 한다.
                <span className={s.correctionMarker} role="img" aria-label="정정 대기" />
              )}
            </div>
          );
        })}
      </div>

      {workDays.length > 0 && (
        <div className={s.summary}>
          <span className={s.summaryText}>
            이번 주 {formatDurationCompact(totalWorkMs)}
            <span className={s.summaryTarget}> / {formatDurationCompact(weeklyTargetMs)}</span>
          </span>
          <span className={s.summaryBar}>
            <span className={s.summaryBarTrack}>
              <span
                className={`${s.summaryBarFill} ${weeklyPct >= 100 ? s.barFillFull : s.barFillHalf}`}
                style={{ width: `${Math.min(weeklyPct, 100)}%` }}
              />
            </span>
            <span className={s.summaryPct}>{weeklyPct}%</span>
          </span>
        </div>
      )}

      <div className={s.legend} aria-label="범례">
        <span className={s.legendItem}>
          <span className={`${s.legendDot} ${s.legendDotFull}`} />
          100%+
        </span>
        <span className={s.legendItem}>
          <span className={`${s.legendDot} ${s.legendDotHalf}`} />
          50%+
        </span>
        <span className={s.legendItem}>
          <span className={`${s.legendDot} ${s.legendDotLow}`} />
          50%↓
        </span>
      </div>
    </div>
  );
}
